import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Modal, ModalBody } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditFooter from '../../common/ModalEditFooter';
import FormDomestic from './FormDomestic';
import FormExport from './FormExport';
import { useTranslation } from 'react-i18next';
import useModalLabels from '../../../hooks/condition/useModalLabels';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createConditionOrdinary, deleteConditionOrdinary, updateConditionOrdinary } from '../../../redux/slices/condition';
import { BUSINESS_TYPE, CONDITION_STATUS, EDIT_ACTION, MANAGE_CONDITION_TYPES } from '../../../constants';
import type { ConditionDetailType, CreateConditionDetailParams, UpdateConditionDetailParams, DeleteConditionDetailParams } from '../../../types/condition';

const ModalEditConditionOrdinary = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dialogMode, isDialogOpen, selectedSteelMaker, selectedBusinessType, condition, conditionOrdinary } = useSelector((state: RootState) => state.condition);
  const [customer, setCustomer] = useState<string>('');
  const [deliveryLocation, setDeliveryLocation] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [hasDouble, setHasDouble] = useState<boolean>(false);
  const { dialogTitle, buttonLabel } = useModalLabels(dialogMode);
  const isDomestic: boolean = selectedBusinessType === BUSINESS_TYPE.DOMESTIC;
  const isExport: boolean = selectedBusinessType === BUSINESS_TYPE.EXPORT;

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  // useEffect(() => {
  //   console.log(customer, deliveryLocation, hasDouble);
  // }, [customer, deliveryLocation, hasDouble]);

  /**
   * 新規作成モードでは初期値を空にしておく
   */
  useEffect(() => {
    if (dialogMode === EDIT_ACTION.CREATE) {
      setCustomer('');
      setDeliveryLocation('');
    }
  }, [dialogMode]);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== EDIT_ACTION.CREATE && conditionOrdinary) {
      setCustomer(conditionOrdinary.customer ?? '');
      setDeliveryLocation(conditionOrdinary.deliveryLocation ?? '');
    }
  }, [conditionOrdinary]);

  /**
   * 通常可視制御の重複チェック
   */
  useEffect(() => {
    if (condition) {
      const sameDetailOrdinary: ConditionDetailType | undefined = condition.detailOrdinary.find((detail) => {
        if (
          // 自分以外に可視制御項目の値が同じものがないか探す
          detail.id !== conditionOrdinary?.id &&
          (detail.customer ?? '') === customer &&
          (detail.deliveryLocation ?? '') === deliveryLocation
        ) {
          return true;
        } else {
          return false;
        }
      });
      if (sameDetailOrdinary) {
        setHasDouble(true);
      } else {
        setHasDouble(false);
      }
    }
  }, [customer, deliveryLocation]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setCustomer('');
    setDeliveryLocation('');
    setDeleteFlag(false);
    setHasDouble(false);
  };

  /**
   * 需要家を変更した時
   */
  const handleChangeCustomer = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // console.log(event);
    const newValue: string = event.target.value;
    setCustomer(newValue);
  };

  /**
   * 受け取り場所を変更した時
   */
  const handleChangeDeliveryLocation = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // console.log(event);
    const newValue: string = event.target.value;
    setDeliveryLocation(newValue);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   * 詳細可視制御とは異なり、設定しない項目はリクエストに含めない（undefinedにする）
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE && condition) {
      const params: CreateConditionDetailParams = {
        conditionId: condition.id,
        isApproved: false,
        conditionType: MANAGE_CONDITION_TYPES.ORDINARY,
        // undefinedにするとリクエストに含まれない（サーバではnullが入る）
        customer: customer.length ? customer : undefined,
        // undefinedにするとリクエストに含まれない（サーバではnullが入る）
        deliveryLocation: deliveryLocation.length ? deliveryLocation : undefined,
        updateCondition: {
          conditionStatus: CONDITION_STATUS.EDITING,
        },
      };
      dispatch(createConditionOrdinary(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && condition && conditionOrdinary) {
      const params: UpdateConditionDetailParams = {
        id: conditionOrdinary.id,
        conditionId: condition.id,
        // undefinedにするとリクエストに含まれない（サーバではnullが入る）
        customer: customer.length ? customer : undefined,
        // undefinedにするとリクエストに含まれない（サーバではnullが入る）
        deliveryLocation: deliveryLocation.length ? deliveryLocation : undefined,
        updateCondition: {
          conditionStatus: CONDITION_STATUS.EDITING,
        },
      };
      dispatch(updateConditionOrdinary(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && condition && conditionOrdinary) {
      const params: DeleteConditionDetailParams = {
        id: conditionOrdinary.id,
        conditionId: condition.id,
        updateCondition: {
          conditionStatus: CONDITION_STATUS.EDITING,
        },
      };
      dispatch(deleteConditionOrdinary(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    // console.log('delete');
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalEditConditionDetail" open={isDialogOpen} maxWidth={'xs'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'visibility'} title={dialogTitle} />
      <ModalBody>
        <Form>
          <FormGroup>
            {isDomestic && (
              <FormDomestic
                steelMaker={selectedSteelMaker}
                labelCustomer={t('condition.customer')}
                labelDeliveryLocation={t('deliveryLocation.deliveryLocation')}
                valueCustomer={customer}
                valueDeliveryLocation={deliveryLocation}
                invalidCustomer={hasDouble}
                invalidDeliveryLocation={hasDouble}
                disabledCustomer={dialogMode === EDIT_ACTION.DELETE}
                disabledDeliveryLocation={dialogMode === EDIT_ACTION.DELETE}
                onChangeCustomer={handleChangeCustomer}
                onChangeDeliveryLocation={handleChangeDeliveryLocation}
              />
            )}
            {isExport && (
              <FormExport
                labelCustomer={t('condition.customer')}
                valueCustomer={customer}
                invalidCustomer={hasDouble}
                disabledCustomer={dialogMode === EDIT_ACTION.DELETE}
                onChangeCustomer={handleChangeCustomer}
              />
            )}
          </FormGroup>
        </Form>
        {hasDouble && <span>{t('dialog.errorDup')}</span>}
      </ModalBody>
      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={(dialogMode === EDIT_ACTION.DELETE && !deleteFlag) || (customer.length === 0 && deliveryLocation.length === 0) || hasDouble}
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditConditionOrdinary;
